class AutoGiftManager {
  constructor() {
    this.settings = window.themeVariables?.settings || {};
    this.giftTiers = this.buildGiftTiers();
    this.isProcessing = false;
    this.lastActionTime = 0;
    this.operationQueue = Promise.resolve();
    this.pendingCartCheck = false;
    this.pendingCartCheckTimeout = null;

    if (this.giftTiers.length === 0) {
      return;
    }

    this.init();
  }

  buildGiftTiers() {
    const tiers = [];

    if (this.settings.cartAutoGiftEnabled === true || this.settings.cartAutoGiftEnabled === 'true') {
      tiers.push({
        key: 'gift1',
        type: 'threshold',
        threshold: parseInt(this.settings.cartAutoGiftThreshold, 10) || 8000,
        productId: this.settings.cartAutoGiftProduct,
        productHandle: this.settings.cartAutoGiftProductHandle,
        firstAvailableVariantId: this.settings.cartAutoGiftProductFirstAvailableVariantId,
        firstVariantId: this.settings.cartAutoGiftProductFirstVariantId,
        message: this.settings.cartAutoGiftMessage || 'Free gift added to your cart!',
        badgeText: this.settings.cartAutoGiftBadgeText || 'Free Gift'
      });
    }

    if (this.settings.cartAutoGiftSecondEnabled === true || this.settings.cartAutoGiftSecondEnabled === 'true') {
      tiers.push({
        key: 'gift2',
        type: 'threshold',
        threshold: parseInt(this.settings.cartAutoGiftSecondThreshold, 10) || 12000,
        productId: this.settings.cartAutoGiftSecondProduct,
        productHandle: this.settings.cartAutoGiftSecondProductHandle,
        firstAvailableVariantId: this.settings.cartAutoGiftSecondProductFirstAvailableVariantId,
        firstVariantId: this.settings.cartAutoGiftSecondProductFirstVariantId,
        message: this.settings.cartAutoGiftSecondMessage || 'Second free gift added to your cart!',
        badgeText: this.settings.cartAutoGiftSecondBadgeText || 'Free Gift'
      });
    }

    if (this.settings.cartSubGiftEnabled === true || this.settings.cartSubGiftEnabled === 'true') {
      tiers.push({
        key: 'gift_sub',
        type: 'subscription_variant',
        triggerVariantIds: this.parseTriggerVariantIds(this.settings.cartSubGiftTriggerVariantId),
        requireSubscription: !(this.settings.cartSubGiftRequireSubscription === false || this.settings.cartSubGiftRequireSubscription === 'false'),
        minQuantity: parseInt(this.settings.cartSubGiftMinQuantity, 10) || 1,
        productId: this.settings.cartSubGiftProduct,
        productHandle: this.settings.cartSubGiftProductHandle,
        firstAvailableVariantId: this.settings.cartSubGiftProductFirstAvailableVariantId,
        firstVariantId: this.settings.cartSubGiftProductFirstVariantId,
        message: this.settings.cartSubGiftMessage || 'Free gift added to your cart!',
        badgeText: this.settings.cartSubGiftBadgeText || 'Geschenk'
      });
    }

    return tiers.filter((tier) => {
      if (!tier.productId) {
        return false;
      }

      if (tier.type === 'subscription_variant') {
        return true;
      }

      return tier.threshold > 0;
    }).sort((a, b) => (a.threshold || 0) - (b.threshold || 0));
  }

  parseTriggerVariantIds(value) {
    return [...new Set(String(value || '')
      .split(',')
      .map((variantId) => variantId.trim())
      .filter(Boolean))];
  }

  init() {
    document.addEventListener('cart:change', (event) => {
      this.handleCartChange(event);
    });

    document.addEventListener('variant:add', (event) => {
      this.handleCartChange(event);
    });

    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => this.checkCartForAutoGift(), 500);
    });

    setTimeout(() => this.checkCartForAutoGift(), 1000);
    setTimeout(() => this.checkCartForAutoGift(), 3000);
  }

  async handleCartChange(event) {
    if (event.detail?.baseEvent === 'auto-gift:add' || event.detail?.baseEvent === 'auto-gift:remove') {
      return;
    }

    if (this.isProcessing) {
      this.queuePendingCartCheck();
      return;
    }

    const cart = event.detail?.cart;

    if (!cart) {
      await this.checkCartForAutoGift();
      return;
    }

    await this.checkCartForAutoGift(cart);
  }

  async checkCartForAutoGift(cartData = null, options = {}) {
    if (this.isProcessing) {
      this.queuePendingCartCheck();
      return this.operationQueue;
    }

    this.operationQueue = this.operationQueue.then(async () => {
      if (this.isProcessing) {
        this.queuePendingCartCheck();
        return;
      }

      const now = Date.now();
      if (now - this.lastActionTime < 500) {
        this.queuePendingCartCheck(500 - (now - this.lastActionTime));
        return;
      }

      this.pendingCartCheck = false;
      return this.executeCartCheck(cartData, options);
    }).catch(() => {});

    return this.operationQueue;
  }

  queuePendingCartCheck(delay = 0) {
    this.pendingCartCheck = true;

    if (this.pendingCartCheckTimeout) {
      return;
    }

    this.pendingCartCheckTimeout = window.setTimeout(() => {
      this.pendingCartCheckTimeout = null;

      if (this.isProcessing || !this.pendingCartCheck) {
        return;
      }

      this.pendingCartCheck = false;
      this.checkCartForAutoGift();
    }, Math.max(delay, 0));
  }

  releaseProcessingLock() {
    window.setTimeout(() => {
      this.isProcessing = false;

      if (!this.pendingCartCheck) {
        return;
      }

      this.pendingCartCheck = false;
      this.checkCartForAutoGift();
    }, 1000);
  }

  async executeCartCheck(cartData = null, options = {}) {
    const forCheckout = options.forCheckout === true;
    let cart = cartData || await this.fetchCart();

    for (const tier of this.giftTiers) {
      const hasGiftProduct = this.hasGiftProductInCart(cart, tier);
      const hasTierGiftProduct = this.hasTierGiftProductInCart(cart, tier);
      const qualifies = this.cartQualifiesForTier(cart, tier);
      const shouldAdd = qualifies && !hasGiftProduct;
      const shouldRemove = !qualifies
        && hasTierGiftProduct
        && !this.hasOtherQualifyingTierForSameProduct(cart, tier);

      if (shouldAdd) {
        await this.addGiftProduct(tier, { forCheckout });
        cart = await this.fetchCart();
      } else if (shouldRemove) {
        await this.removeGiftProduct(cart, tier, { forCheckout });
        cart = await this.fetchCart();
      }
    }
  }

  cartQualifiesForTier(cart, tier) {
    if (tier.type === 'subscription_variant') {
      return this.cartHasQualifyingSubscriptionVariant(cart, tier);
    }

    return cart.total_price >= tier.threshold;
  }

  cartHasQualifyingSubscriptionVariant(cart, tier) {
    return this.getQualifyingSubscriptionQuantity(cart, tier) >= (tier.minQuantity > 0 ? tier.minQuantity : 1);
  }

  isSubscriptionCardsGiftItem(item, tier) {
    // Only the Blitz-Deal 3-pack bought through the cards template extends this tier.
    // The private property survives navigation, so the gift also stays eligible in the cart.
    return String(item.variant_id || item.id) === '52835646537995'
      && String(item.properties?._subscription_cards_gift || '') === String(tier.productId);
  }

  getQualifyingSubscriptionQuantity(cart, tier) {
    const triggerVariantIds = Array.isArray(tier.triggerVariantIds)
      ? tier.triggerVariantIds.map(String)
      : this.parseTriggerVariantIds(tier.triggerVariantId);

    return cart.items.reduce((total, item) => {
      if (triggerVariantIds.length > 0 && !triggerVariantIds.includes(item.variant_id.toString()) && !this.isSubscriptionCardsGiftItem(item, tier)) {
        return total;
      }

      // Never let the gift product itself satisfy its own trigger.
      if (item.properties && item.properties._auto_gift === 'true') {
        return total;
      }

      // A subscription line item carries a selling_plan_allocation in cart.js.
      if (tier.requireSubscription && !item.selling_plan_allocation) {
        return total;
      }

      return total + item.quantity;
    }, 0);
  }

  getPendingSubscriptionQuantity(pendingItems, tier) {
    const triggerVariantIds = Array.isArray(tier.triggerVariantIds)
      ? tier.triggerVariantIds.map(String)
      : this.parseTriggerVariantIds(tier.triggerVariantId);

    return pendingItems.reduce((total, item) => {
      if (triggerVariantIds.length > 0 && !triggerVariantIds.includes(String(item.id)) && !this.isSubscriptionCardsGiftItem(item, tier)) {
        return total;
      }

      if (tier.requireSubscription && !item.selling_plan) {
        return total;
      }

      return total + Math.max(1, parseInt(item.quantity, 10) || 1);
    }, 0);
  }

  getPendingItemsPrice(pendingItems) {
    let total = 0;

    for (const item of pendingItems) {
      const price = Number(item._autoGiftPrice);
      if (!Number.isFinite(price)) {
        return null;
      }

      total += price * (Math.max(1, parseInt(item.quantity, 10) || 1));
    }

    return total;
  }

  async getGiftItemsForCartAdd(pendingItems = []) {
    if (this.giftTiers.length === 0) {
      return [];
    }

    try {
      if (this.isProcessing) {
        await this.operationQueue;
      }

      const cart = await this.fetchCart();
      const pendingGiftProductIds = new Set();
      const pendingPrice = this.getPendingItemsPrice(pendingItems);
      const giftItems = [];

      for (const tier of this.giftTiers) {
        const productId = String(tier.productId);
        if (this.hasGiftProductInCart(cart, tier) || pendingGiftProductIds.has(productId)) {
          continue;
        }

        let qualifies = false;
        if (tier.type === 'subscription_variant') {
          const currentQuantity = this.getQualifyingSubscriptionQuantity(cart, tier);
          const pendingQuantity = this.getPendingSubscriptionQuantity(pendingItems, tier);
          qualifies = currentQuantity + pendingQuantity >= (tier.minQuantity > 0 ? tier.minQuantity : 1);
        } else if (pendingPrice !== null) {
          qualifies = cart.total_price + pendingPrice >= tier.threshold;
        }

        if (!qualifies) {
          continue;
        }

        const selectedVariantId = await this.resolveVariantId(tier);
        if (!selectedVariantId) {
          continue;
        }

        giftItems.push({
          id: selectedVariantId,
          quantity: 1,
          properties: {
            '_auto_gift': 'true',
            '_gift_tier': tier.key,
            '_gift_message': tier.message,
            '_gift_badge_text': tier.badgeText
          }
        });
        pendingGiftProductIds.add(productId);
      }

      return giftItems;
    } catch (_) {
      return [];
    }
  }

  async fetchCart() {
    const response = await fetch(`${Shopify.routes.root}cart.js`);
    return response.json();
  }

  findGiftProductInCart(cart, tier) {
    return cart.items.find((item) => {
      return item.product_id.toString() === tier.productId.toString()
        && item.properties
        && item.properties._auto_gift === 'true'
        && (!item.properties._gift_tier || item.properties._gift_tier === tier.key);
    }) || null;
  }

  hasTierGiftProductInCart(cart, tier) {
    return Boolean(this.findGiftProductInCart(cart, tier));
  }

  hasOtherQualifyingTierForSameProduct(cart, tier) {
    return this.giftTiers.some((candidateTier) => {
      if (candidateTier.key === tier.key) {
        return false;
      }

      if (candidateTier.productId.toString() !== tier.productId.toString()) {
        return false;
      }

      return this.cartQualifiesForTier(cart, candidateTier);
    });
  }

  hasGiftProductInCart(cart, tier) {
    const autoGiftItems = cart.items.filter((item) => {
      return item.product_id.toString() === tier.productId.toString() && item.properties && item.properties._auto_gift === 'true';
    });

    if (autoGiftItems.length > 0) {
      return true;
    }

    return cart.items.some((item) => item.product_id.toString() === tier.productId.toString());
  }

  async resolveVariantId(tier) {
    if (tier.firstAvailableVariantId || tier.firstVariantId) {
      return tier.firstAvailableVariantId || tier.firstVariantId;
    }

    const handlePath = tier.productHandle ? tier.productHandle : tier.productId;
    const response = await fetch(`${Shopify.routes.root}products/${handlePath}.js`);

    if (!response.ok) {
      return null;
    }

    const product = await response.json();
    if (!product || !product.variants || product.variants.length === 0) {
      return null;
    }

    const availableVariant = product.variants.find((variant) => variant.available) || product.variants[0];
    return availableVariant.id;
  }

  async addGiftProduct(tier, options = {}) {
    const forCheckout = options.forCheckout === true;

    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      const preCheckCart = await this.fetchCart();
      const existingProduct = preCheckCart.items.some((item) => item.product_id.toString() === tier.productId.toString());

      if (existingProduct) {
        return;
      }

      const selectedVariantId = await this.resolveVariantId(tier);
      if (!selectedVariantId) {
        return;
      }

      const addResponse = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: selectedVariantId,
          quantity: 1,
          properties: {
            '_auto_gift': 'true',
            '_gift_tier': tier.key,
            '_gift_message': tier.message,
            '_gift_badge_text': tier.badgeText
          }
        })
      });

      if (!addResponse.ok) {
        return;
      }

      this.lastActionTime = Date.now();
      if (forCheckout) {
        return;
      }

      const updatedCart = await this.fetchBundledCart();
      this.dispatchCartChange('auto-gift:add', updatedCart);
    } catch (_) {
      // Intentionally swallowed to keep cart UX resilient.
    } finally {
      this.releaseProcessingLock();
    }
  }

  async removeGiftProduct(cart, tier, options = {}) {
    const forCheckout = options.forCheckout === true;

    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    try {
      const giftItem = this.findGiftProductInCart(cart, tier);

      if (!giftItem) {
        return;
      }

      const removeResponse = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0
        })
      });

      if (!removeResponse.ok) {
        return;
      }

      this.lastActionTime = Date.now();
      if (forCheckout) {
        return;
      }

      const updatedCart = await this.fetchBundledCart();
      this.dispatchCartChange('auto-gift:remove', updatedCart);
    } catch (_) {
      // Intentionally swallowed to keep cart UX resilient.
    } finally {
      this.releaseProcessingLock();
    }
  }

  async fetchBundledCart() {
    const sectionsToBundle = [];

    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    const updatedCart = await this.fetchCart();

    if (sectionsToBundle.length === 0) {
      return updatedCart;
    }

    try {
      const sectionsResponse = await fetch(`${Shopify.routes.root}?sections=${sectionsToBundle.join(',')}`);
      const sectionsText = await sectionsResponse.text();

      try {
        updatedCart.sections = JSON.parse(sectionsText);
      } catch (_) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = sectionsText;
        updatedCart.sections = {};

        sectionsToBundle.forEach((sectionId) => {
          const sectionElement = tempDiv.querySelector(`#shopify-section-${sectionId}`);
          if (sectionElement) {
            updatedCart.sections[sectionId] = sectionElement.outerHTML;
          }
        });
      }
    } catch (_) {
      // Keep cart data even if section bundle refresh fails.
    }

    return updatedCart;
  }

  dispatchCartChange(baseEvent, cart) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent,
        cart
      }
    }));
  }
}

let autoGiftManagerInstance = null;
let resolveAutoGiftManagerReady;
const autoGiftManagerReady = new Promise((resolve) => {
  resolveAutoGiftManagerReady = resolve;
});

window.ensureAutoGiftForCheckout = async function() {
  const manager = await autoGiftManagerReady;
  if (!manager || manager.giftTiers.length === 0) {
    return;
  }

  await manager.checkCartForAutoGift(null, { forCheckout: true });
};

window.getAutoGiftItemsForCartAdd = async function(pendingItems = []) {
  const manager = await autoGiftManagerReady;
  if (!manager) {
    return [];
  }

  return manager.getGiftItemsForCartAdd(pendingItems);
};

function initializeAutoGift() {
  if (autoGiftManagerInstance) {
    return;
  }

  if (typeof Shopify === 'undefined' || !window.themeVariables) {
    setTimeout(initializeAutoGift, 500);
    return;
  }

  autoGiftManagerInstance = new AutoGiftManager();
  resolveAutoGiftManagerReady(autoGiftManagerInstance);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAutoGift);
} else {
  initializeAutoGift();
}

setTimeout(() => {
  initializeAutoGift();
}, 2000);
